<!-- DebugOverlay.vue -->
<template>
    <ButtonPrimary v-if="!open" class="debug-button-open" :onClick="toggleOpen" size="s">
        Show logs
    </ButtonPrimary>

    <div v-if="open" class="debug-container">
        <div class="content">
            <div class="header">
                <ButtonPrimary :onClick="toggleOpen" size="s"> Hide logs </ButtonPrimary>
                <ButtonIcon
                    v-if="open"
                    :onClick="clearLog"
                    ariaLabel="Delete"
                    variant="filled"
                    icon="Bin"
                    size="s" />
            </div>

            <div v-if="open">
                <div v-for="(e, i) in entries" :key="i" class="entry">
                    <span class="timestamp">[{{ e.ts }}]</span>
                    <strong
                        :class="['level', e.level === 'error' ? 'levelError' : 'levelLog']">
                        {{ e.level.toUpperCase() }}
                    </strong>
                    {{ e.msg }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, ref, shallowRef, onMounted, onBeforeUnmount } from 'vue';
    import ButtonIcon from '@ui/components/Button/ButtonIcon.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';

    type Entry = { level: 'log' | 'error'; msg: string; ts: string };
    type ConsoleFn = (...data: unknown[]) => void;
    type ConsoleMethods = { log: ConsoleFn; error: ConsoleFn };

    const MAX = 200;

    export default defineComponent({
        name: 'DebugOverlay',
        components: { ButtonPrimary, ButtonIcon },
        props: {
            showUnhandledError: { type: Boolean, default: false },
        },
        setup(props) {
            const open = ref(false);
            const entries = ref<Entry[]>([]);
            const original = shallowRef<ConsoleMethods | null>(null);

            // Listeners kept here so we can remove them in onBeforeUnmount
            let onErr: ((e: ErrorEvent) => void) | null = null;
            let onRej: ((e: PromiseRejectionEvent) => void) | null = null;

            const toggleOpen = () => {
                open.value = !open.value;
            };

            const clearLog = () => {
                entries.value = [];
            };

            const push = (level: Entry['level'], args: unknown[]) => {
                const ts = new Date().toISOString().split('T')[1]?.replace('Z', '') ?? '';
                const msg = args
                    .map((a) => {
                        try {
                            return typeof a === 'string' ? a : JSON.stringify(a);
                        } catch {
                            return String(a);
                        }
                    })
                    .join(' ');
                const next = [...entries.value, { level, msg, ts }];
                entries.value = next.length > MAX ? next.slice(next.length - MAX) : next;
            };

            onMounted(() => {
                if (original.value) return;

                original.value = {
                    log: console.log.bind(console) as ConsoleFn,
                    error: console.error.bind(console) as ConsoleFn,
                };

                console.log = (...a: unknown[]) => {
                    push('log', a);
                    original.value!.log(...a);
                };

                console.error = (...a: unknown[]) => {
                    push('error', a);
                    original.value!.error(...a);
                };

                if (props.showUnhandledError) {
                    onErr = (e: ErrorEvent) =>
                        console.error('[window.onerror]', e.message, e.filename, e.lineno);
                    onRej = (e: PromiseRejectionEvent) =>
                        console.error('[unhandledrejection]', e.reason);

                    window.addEventListener('error', onErr);
                    window.addEventListener('unhandledrejection', onRej);
                }
            });

            onBeforeUnmount(() => {
                if (original.value) {
                    console.log = original.value.log;
                    console.error = original.value.error;
                    original.value = null;
                }
                if (onErr) window.removeEventListener('error', onErr);
                if (onRej) window.removeEventListener('unhandledrejection', onRej);
            });

            return { open, entries, toggleOpen, clearLog };
        },
    });
</script>
<style lang="scss" scoped>
    @forward './DebugOverlay.scss';
</style>
